import { useTranslation } from 'react-i18next'
import type { z } from 'zod'
import { useStandortForm } from '@/features/befehlsnachrichten/hooks/useStandortForm'
import {
  Button,
  DEFAULT_INPUT_MAX_LENGTH,
  BETRIEBSSTELLE_MAX_LENGTH,
  ErrorAlert,
  Gleisart,
  Heading,
  Input,
  Ort,
  Standort,
  Wegpunkt
} from '@repo/ui'
import Radio from '@/components/common/form/Radio'
import FormErrorMessage from '@/components/common/form/FormErrorMessage'
import { Befehlsnachricht } from '@/models/Befehlsnachricht'
import { Abruftoken } from '@/models/Abruftoken'
import { checkAbruftokenExpired } from '@/features/befehlsnachrichten/utils/abruftoken'
import useBfnStandortMutation from '@/features/befehlsnachrichten/mutations/useBfnStandortMutation'
import useOnlineDetection from '@/features/offline/hooks/useOnlineDetection'
import BfnKopfdatenBox from './BfnKopfdatenBox'

interface BfnStandorteingabeProps {
  abruftoken: Abruftoken
  bfn: Befehlsnachricht
  'data-testid': string
}

export default function BfnStandorteingabe({
  abruftoken,
  bfn,
  'data-testid': testId
}: Readonly<BfnStandorteingabeProps>) {
  const { t } = useTranslation('standorteingabe')
  const isTokenExpired = checkAbruftokenExpired(abruftoken)
  const { mutation } = useBfnStandortMutation(bfn, abruftoken)

  const isOnline = useOnlineDetection()

  const { register, watch, handleSubmit, errors } = useStandortForm()

  type FormData = z.infer<ReturnType<typeof useStandortForm>['formDataSchema']>

  const wegpunkt = watch('wegpunkt')
  const ort = watch('ort')

  const onSubmit = (formData: FormData) => {
    const standort: Standort = {
      wegpunkt: formData.wegpunkt,
      ort: formData.ort,
      gleisart: 'gleisart' in formData ? formData.gleisart : null,
      von: 'von' in formData ? formData.von : null,
      nach: 'nach' in formData ? formData.nach : null,
      gleis: 'gleis' in formData ? formData.gleis : null,
      betriebsstelle:
        'betriebsstelle' in formData ? formData.betriebsstelle : null,
      kilometrierung:
        'kilometrierung' in formData ? formData.kilometrierung : null,
      weiche: 'weiche' in formData ? formData.weiche : null,
      signal: 'signal' in formData ? formData.signal : null
    }

    mutation.mutate(standort)
  }

  return (
    <>
      <Heading tag="h1">{t('title')}</Heading>
      <BfnKopfdatenBox
        bfn={bfn}
        className="mb-2"
        data-testid={`${testId}-zugdatenbox`}
      />

      {isTokenExpired && (
        <ErrorAlert data-testid={`${testId}-error`}>
          {' '}
          {t('errors.token_expired')}
        </ErrorAlert>
      )}
      {!isTokenExpired && (
        <form onSubmit={handleSubmit(onSubmit)}>
          {mutation.isError && (
            <ErrorAlert className="mb-2" data-testid={`${testId}-error`}>
              {mutation.error.message}
            </ErrorAlert>
          )}

          <p className="mb-1" data-testid={`${testId}-type-text`}>
            {t('type_text')}
          </p>

          {/* Wegpunkt */}
          <div className="flex flex-col gap-1 mb-2.5">
            <Radio
              label={t('radio_signal_label')}
              value={Wegpunkt.Signal}
              {...register('wegpunkt')}
              data-testid={`${testId}-signal`}
            />
            <Radio
              label={t('radio_km_label')}
              value={Wegpunkt.Kilometrierung}
              {...register('wegpunkt')}
              data-testid={`${testId}-kilometrierung`}
            />
            <Radio
              label={t('radio_switch_label')}
              value={Wegpunkt.Weiche}
              {...register('wegpunkt')}
              data-testid={`${testId}-weiche`}
            />
            <FormErrorMessage
              errors={errors}
              name="wegpunkt"
              data-testid={`${testId}-wegpunkt-error`}
            />
          </div>

          {/* Signal */}
          {wegpunkt === Wegpunkt.Signal && (
            <div className="flex flex-col items-start mb-2.5">
              <div className="flex gap-1">
                <Input
                  maxLength={DEFAULT_INPUT_MAX_LENGTH}
                  className="flex-1"
                  type="text"
                  label={t('signal_label')}
                  variant="solid"
                  errors={errors}
                  {...register('signal')}
                  data-testid={`${testId}-signal-input`}
                />
              </div>
            </div>
          )}

          {/* Kilometrierung */}
          {wegpunkt === Wegpunkt.Kilometrierung && (
            <div className="flex flex-col items-start mb-2.5">
              <div className="flex gap-1">
                <Input
                  maxLength={DEFAULT_INPUT_MAX_LENGTH}
                  className="flex-1"
                  type="text"
                  label={t('km_stand_input_label')}
                  variant="solid"
                  errors={errors}
                  {...register('kilometrierung')}
                  data-testid={`${testId}-kilometrierung`}
                />
              </div>
            </div>
          )}

          {/* Weiche */}
          {wegpunkt === Wegpunkt.Weiche && (
            <div className="flex flex-col items-start mb-2.5">
              <Input
                maxLength={DEFAULT_INPUT_MAX_LENGTH}
                type="text"
                label={t('radio_switch_label')}
                variant="solid"
                errors={errors}
                {...register('weiche')}
                data-testid={`${testId}-weiche`}
              />
            </div>
          )}
          {wegpunkt && (
            <>
              {/* ORT */}
              <div className="flex flex-col items-start gap-1 mb-2.5">
                <p data-testid={`${testId}-ort`}>{t('train_location_text')}</p>
                {/* ---- Ort ---- */}
                <Radio
                  label={t('radio_location_rail_label')}
                  value={Ort.Strecke}
                  {...register('ort')}
                  data-testid={`${testId}-ort-strecke`}
                />
                <Radio
                  label={t('radio_location_train_station_label')}
                  value={Ort.Bahnhof}
                  {...register('ort')}
                  data-testid={`${testId}-ort-bahnhof`}
                />
                <FormErrorMessage
                  errors={errors}
                  name="ort"
                  data-testid={`${testId}-ort-error`}
                />
              </div>
              {/* ---- Gleis Art ---- */}
              {ort === Ort.Strecke && (
                <>
                  <div className="flex flex-col items-start gap-1 mb-2.5">
                    <p data-testid={`${testId}-gleisart-text`}>{t('auf')}</p>
                    <Radio
                      label={t('normal_rail_type_label')}
                      value={Gleisart.Regelgleis}
                      {...register('gleisart')}
                      data-testid={`${testId}-gleisart-regelgleis`}
                    />
                    <Radio
                      label={t('opposite_rail_type_label')}
                      value={Gleisart.Gegengleis}
                      {...register('gleisart')}
                      data-testid={`${testId}-gleisart-gegengleis`}
                    />
                    <Radio
                      label={t('just_rail_type_label')}
                      value={Gleisart.Gleis}
                      {...register('gleisart')}
                      data-testid={`${testId}-gleisart-gleis`}
                    />
                    <FormErrorMessage
                      errors={errors}
                      name="gleisart"
                      data-testid={`${testId}-gleisart-error`}
                    />
                  </div>
                  <div className="flex flex-col items-start gap-0.5 mb-3">
                    <Input
                      maxLength={BETRIEBSSTELLE_MAX_LENGTH}
                      className="mb-0.5"
                      type="text"
                      label={t('from_operating_point')}
                      variant="solid"
                      errors={errors}
                      {...register('von')}
                      data-testid={`${testId}-betriebsstelle-von`}
                    />
                    <Input
                      maxLength={BETRIEBSSTELLE_MAX_LENGTH}
                      type="text"
                      label={t('to_operating_point')}
                      variant="solid"
                      errors={errors}
                      {...register('nach')}
                      data-testid={`${testId}-betriebsstelle-nach`}
                    />
                  </div>
                </>
              )}
              {ort === Ort.Bahnhof && (
                <div className="flex flex-col items-start gap-1 mb-2.5">
                  <Input
                    maxLength={DEFAULT_INPUT_MAX_LENGTH}
                    type="text"
                    label={t('train_station_rail_number_label')}
                    variant="solid"
                    errors={errors}
                    {...register('gleis')}
                    data-testid={`${testId}-gleis`}
                  />
                  <Input
                    maxLength={BETRIEBSSTELLE_MAX_LENGTH}
                    type="text"
                    label={t('train_station_operating_point_label')}
                    variant="solid"
                    errors={errors}
                    {...register('betriebsstelle')}
                    data-testid={`${testId}-betriebsstelle`}
                  />
                </div>
              )}
            </>
          )}

          <div className="flex flex-col bottom-0 md:block md:text-right">
            <Button
              variant="brand-primary"
              type="submit"
              data-testid={`${testId}-submit-button`}
              disabled={!isOnline || mutation.isPending}
            >
              {t('apply_button')}
            </Button>
          </div>
        </form>
      )}
    </>
  )
}
